import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../../app/store';
import { 
  updateScheduledProcedureCodeAction as updateScheduledProcedureCode, 
  removeScheduledProcedureCodeAction as removeScheduledProcedureCode 
} from '../../../../../../services/EncounterService/slice';
import { useGetProceduresQuery } from '../../../../../../services/ConfigurationService';
import { useScheduledProcedureCodeEditForm } from './useScheduledProcedureCodeEditForm';
import type { ScheduledProcedureCodeType } from '../../../../../../services/EncounterService/types/encounterTypes';

interface UseScheduledProcedureCodeEditDialogProps {
  open: boolean;
  scheduledProcedureCode: ScheduledProcedureCodeType | null;
  onClose: () => void;
  onScheduledProcedureCodeUpdated?: () => void;
}

export const useScheduledProcedureCodeEditDialog = ({ 
  open, 
  scheduledProcedureCode, 
  onClose, 
  onScheduledProcedureCodeUpdated 
}: UseScheduledProcedureCodeEditDialogProps) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const encounter = useAppSelector(state => state.encounter.currentEncounter);
  
  // Check if encounter is cancelled
  const isEncounterCancelled = encounter?.status === 'Cancelled';

  // Get center ID from URL parameters
  const company = searchParams.get('company');
  const center = searchParams.get('center');
  const centerId = company && center ? `${company}-${center}` : '';

  // Fetch procedures from configuration API
  const { data: procedures = [], isLoading: proceduresLoading } = useGetProceduresQuery(
    { centerId },
    { skip: !centerId }
  );

  // Form state
  const formState = useScheduledProcedureCodeEditForm(scheduledProcedureCode, open, procedures);

  const handleClose = useCallback(() => {
    onClose();
    formState.setError('');
  }, [onClose, formState]);

  const handleSubmit = useCallback(() => {
    if (!formState.validateForm()) return;

    if (!scheduledProcedureCode?.id) {
      formState.setError('Scheduled Procedure Code ID is required for updates');
      return;
    }

    dispatch(updateScheduledProcedureCode({ id: scheduledProcedureCode.id, updates: formState.formData }));
    onScheduledProcedureCodeUpdated?.();
    handleClose();
  }, [dispatch, formState, handleClose, onScheduledProcedureCodeUpdated, scheduledProcedureCode?.id]);

  const handleDelete = useCallback(() => {
    if (!scheduledProcedureCode?.id) return;
    
    dispatch(removeScheduledProcedureCode(scheduledProcedureCode.id));
    handleClose();
  }, [dispatch, handleClose, scheduledProcedureCode?.id]);

  return {
    procedures,
    proceduresLoading,
    isEncounterCancelled,
    handleClose,
    handleSubmit,
    handleDelete,
    ...formState,
  };
};
